/**
 * React 17 Compatibility Layer
 *
 * This module provides polyfills for React 18+ features for backward compatibility.
 *
 * HYDRATION ISSUE FIX:
 * The useId hook implementation uses a seeded counter to prevent hydration mismatches
 * between server-rendered and client-rendered components. Without proper seeding,
 * the idCounter would reset on each import, causing different IDs to be generated
 * on the server vs client, leading to hydration warnings.
 *
 * USAGE IN SSR:
 * Call resetIdSeed() at the beginning of each SSR request to ensure unique IDs:
 *
 * @example
 * // Server entry point
 * import { resetIdSeed } from './shadcn/components/command/react-compat';
 *
 * app.use((req, res, next) => {
 *   resetIdSeed(); // Reset for each request
 *   next();
 * });
 */
import { useRef } from 'react';
import { useSyncExternalStore as useSyncExternalStoreShim } from 'use-sync-external-store/shim';

// React 17 polyfill for useId
// Seeded counter to prevent hydration mismatches between server and client
let idSeed = Math.floor(performance.now() * 1000) % 1000000; // Use performance.now for initial seed
let idCounter = 0;

/**
 * Resets the ID seed to ensure unique IDs across different SSR requests.
 * This function should be called at the beginning of each SSR request
 * to prevent hydration warnings caused by ID mismatches.
 *
 * @example
 * // In your server entry point
 * import { resetIdSeed } from './react-compat';
 *
 * app.use((req, res, next) => {
 *   resetIdSeed();
 *   next();
 * });
 */
export const resetIdSeed = (): void => {
  // Generate a new seed using current timestamp and random factor
  idSeed = Math.floor((performance.now() + Math.random() * 1000) * 1000) % 1000000;
  idCounter = 0;
};

export const useId = () => {
  const idRef = useRef<string | null>(null);

  if (idRef.current === null) {
    idRef.current = `:r${idSeed}-${idCounter++}:`;
  }

  return idRef.current;
};

// React 17 polyfill for useSyncExternalStore
export const useSyncExternalStore = useSyncExternalStoreShim;

// Utility for creating a unique ID for each item
export const createItemId = (value: string) => {
  return value.replace(/\s/g, '-').toLowerCase();
};
